import { useEffect, useState } from 'react';

import { Box, Typography, Skeleton, Paper, Divider, Button } from '@mui/material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { client, getMissingDataApiV1MissingDataDataTypeGet } from '../../client';
import { RenderDataTable } from '../../components/common/RenderDataTable';
import PageLayout from '../../components/PageLayout/PageLayout';
import { useAuth } from '../../services/auth/AuthContext';
import NotificationService from '../../services/notification/NotificationService';

const DataIssuesPage = () => {
  const { getToken } = useAuth();
  const [authTokenLoaded, setAuthTokenLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const setupAuth = async () => {
      const token = await getToken();
      client.setConfig({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAuthTokenLoaded(true);
    };

    setupAuth();
  }, [getToken]);

  const {
    data: productGroupData,
    isLoading: productGroupLoading,
    isError: productGroupError,
    error: productGroupErrorDetails,
  } = useQuery(
    ['missingData', 'product_group'],
    () =>
      getMissingDataApiV1MissingDataDataTypeGet({
        path: { data_type: 'product_group' },
      }),
    {
      enabled: authTokenLoaded,
      onSuccess: (data) => {
        if (data.data) {
          NotificationService.success('Product group missing data loaded successfully');
        }
      },
      onError: (error) => {
        NotificationService.error(`Error loading product group data: ${error}`);
      },
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: buyerByProductData,
    isLoading: buyerByProductLoading,
    isError: buyerByProductError,
    error: buyerByProductErrorDetails,
  } = useQuery(
    ['missingData', 'buyer_by_product'],
    () =>
      getMissingDataApiV1MissingDataDataTypeGet({
        path: { data_type: 'buyer_by_product' },
      }),
    {
      enabled: authTokenLoaded,
      onSuccess: (data) => {
        if (data.data) {
          NotificationService.success('Buyer by product missing data loaded successfully');
        }
      },
      onError: (error) => {
        NotificationService.error(`Error loading buyer by product data: ${error}`);
      },
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: customerGroupData,
    isLoading: customerGroupLoading,
    isError: customerGroupError,
    error: customerGroupErrorDetails,
  } = useQuery(
    ['missingData', 'customer_group'],
    () =>
      getMissingDataApiV1MissingDataDataTypeGet({
        path: { data_type: 'customer_group' },
      }),
    {
      enabled: authTokenLoaded,
      onSuccess: (data) => {
        if (data.data) {
          NotificationService.success('Customer group missing data loaded successfully');
        }
      },
      onError: (error) => {
        NotificationService.error(`Error loading customer group data: ${error}`);
      },
      refetchOnWindowFocus: false,
    },
  );

  return (
    <PageLayout pageId={'data_issues'}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Data Issues
        </Typography>
        <Typography variant="body1" paragraph>
          This page shows missing data mappings that need to be resolved.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Missing Product Groups
          </Typography>
          {productGroupLoading ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Skeleton variant="rectangular" height={300} />
            </Paper>
          ) : productGroupError ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography color="error">
                Error loading product group data: {JSON.stringify(productGroupErrorDetails)}
              </Typography>
            </Paper>
          ) : (
            productGroupData?.data?.missing_items && (
              <>
                <RenderDataTable
                  data={productGroupData.data.missing_items}
                  title="Missing Product Group Mappings"
                  noDataFoundMessage="All data fixed!"
                />
                {productGroupData.data.missing_items.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate('/issues/fix_product_group')}
                    >
                      Fix Product Group Issues
                    </Button>
                  </Box>
                )}
              </>
            )
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Missing Buyer by Product Mappings
          </Typography>
          {buyerByProductLoading ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Skeleton variant="rectangular" height={300} />
            </Paper>
          ) : buyerByProductError ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography color="error">
                Error loading buyer by product data: {JSON.stringify(buyerByProductErrorDetails)}
              </Typography>
            </Paper>
          ) : (
            buyerByProductData?.data?.missing_items && (
              <>
                <RenderDataTable
                  data={buyerByProductData.data.missing_items}
                  title="Missing Buyer by Product Mappings"
                  noDataFoundMessage="All data fixed!"
                />
                {buyerByProductData.data.missing_items.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate('/issues/fix_buyer_by_product')}
                    >
                      Fix Buyer by Product Issues
                    </Button>
                  </Box>
                )}
              </>
            )
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Missing Customer Group Mappings
          </Typography>
          {customerGroupLoading ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Skeleton variant="rectangular" height={300} />
            </Paper>
          ) : customerGroupError ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography color="error">
                Error loading customer group data: {JSON.stringify(customerGroupErrorDetails)}
              </Typography>
            </Paper>
          ) : (
            customerGroupData?.data?.missing_items && (
              <>
                <RenderDataTable
                  data={customerGroupData.data.missing_items}
                  title="Missing Customer Group Mappings"
                  noDataFoundMessage="All data fixed!"
                />
                {customerGroupData.data.missing_items.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate('/issues/fix_customer_group')}
                    >
                      Fix Customer Group Issues
                    </Button>
                  </Box>
                )}
              </>
            )
          )}
        </Box>
      </Box>
    </PageLayout>
  );
};

export default DataIssuesPage;
